import type { Phone } from '../types'

export type SortKey = 'name' | 'status' | 'battery'
export type SortDir = 'asc' | 'desc'

export interface SortState {
  key: SortKey
  dir: SortDir
}

const STATUS_ORDER: Record<Phone['status'], number> = {
  online: 0,
  'in-call': 1,
  reconnecting: 2,
  offline: 3,
}

/**
 * Three-way comparator that always pushes null to the end regardless of
 * sort direction. `cmp` is invoked only when both sides are non-null.
 */
function nullsLast<T>(
  a: T | null,
  b: T | null,
  cmp: (a: T, b: T) => number,
): number {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  return cmp(a, b)
}

const COMPARATORS: Record<SortKey, (a: Phone, b: Phone, sign: number) => number> = {
  name: (a, b, sign) => a.name.localeCompare(b.name) * sign,
  status: (a, b, sign) => {
    const oa = STATUS_ORDER[a.status]
    const ob = STATUS_ORDER[b.status]
    if (oa !== ob) return (oa - ob) * sign
    return a.name.localeCompare(b.name)
  },
  battery: (a, b, sign) =>
    nullsLast(
      a.metrics?.batteryPct ?? null,
      b.metrics?.batteryPct ?? null,
      (x, y) => (x - y) * sign,
    ),
}

/**
 * Sort `phones` by `sort` key. Null metrics always sort last so a fresh
 * phone without telemetry doesn't surface first when admin sorts by
 * battery.
 */
export function sortPhones(phones: Phone[], sort: SortState): Phone[] {
  const sign = sort.dir === 'asc' ? 1 : -1
  const cmp = COMPARATORS[sort.key]
  return phones.slice().sort((a, b) => cmp(a, b, sign))
}
